import { useCallback } from "react";
import { ChatMessage } from "../types/chat";
import { CHAT_CONSTANTS } from "../utils/constants";
import { makeLogRequest } from "../services/ChatService";
import { getCurrentSessionId, getCurrentPSTDateTime } from "../utils/apiUtils";
import { useAuth } from "../providers/AuthContext";
import { useMessages } from "../providers/MessagesContext";
import { getLastBotMessage } from "../utils/messageUtils";
import { wrapTyping } from "../utils/typingGate";

export const useChatActions = () => {
  const { getUserEmail } = useAuth();
  const {
    addMessage,
    addMessageObject,
    addToStack,
    setGoBackStack,
    messagesRef,
    goBackStackRef,
  } = useMessages();
  const handleGoBack = useCallback(
    async (queryId?: string) => {
      if (goBackStackRef.current.length < 2) return;

      const userMessageId = crypto.randomUUID();

      await wrapTyping(async () => {
        try {
          const newStack = [...goBackStackRef.current];
          newStack.pop();
          const secondItem = newStack.pop();
          setGoBackStack(newStack);

          if (!queryId) {
            // if user clicked go back button, there will be no queryId parameter since it's a new row in the dynamoDb table
            // whereas if the user typed something akin to go back and the compare response matched it, then there is a queryID corresponding to a dynamoDB table row to update
            addMessage("Go Back", "user", userMessageId);
          }

          if (
            secondItem &&
            secondItem.nodeUuid !== CHAT_CONSTANTS.PLACEHOLDER_UUID
          ) {
            const messageItem = messagesRef.current.find(
              (msg) =>
                msg.messageId === secondItem.messageId &&
                msg.messageType === "bot"
            );

            if (messageItem) {
              // Get the last bot message to find the node UUID the user is replying to
              const lastBotMessage = getLastBotMessage(messagesRef.current);
              const userReplyingToNodeUuid = lastBotMessage?.nodeUuid || "";

              // Extract primitive values to avoid circular references
              const messageNodeUuid = messageItem.nodeUuid || "";
              const messageText = messageItem.text || "";
              const messageAudio = messageItem.audio
                ? Array.isArray(messageItem.audio)
                  ? messageItem.audio[0]
                  : messageItem.audio
                : "";
              const messageMedia = messageItem.media || "";
              const messageOrigin = messageItem.origin || "";
              const messageOptions = messageItem.options || [];

              // Create a clean payload object to avoid circular references
              const finalLogPayload = queryId
                ? {
                    query_id: queryId,
                    elapsed_time_to_fetch_audio: messageAudio
                      ? "Already fetched before"
                      : "",
                    elapsed_time_to_fetch_media: messageMedia
                      ? "Already fetched before"
                      : "",
                    session_id: getCurrentSessionId(),
                    bot_message_reply_uuid: messageNodeUuid,
                    bot_message_reply_text: messageText,
                    bot_message_reply_audio: messageAudio,
                    bot_message_reply_media: messageMedia,
                    bot_message_reply_origin: messageOrigin,
                    bot_message_reply_options: messageOptions,
                    bot_message_reply_datetime: getCurrentPSTDateTime(),
                    user_email: getUserEmail(),
                    user_replying_to_node_uuid: userReplyingToNodeUuid,
                  }
                : {
                    query_id: crypto.randomUUID().toString(), // New query ID for button click
                    bot_message_reply_uuid: messageNodeUuid,
                    bot_message_reply_text: messageText,
                    bot_message_reply_audio: messageAudio,
                    bot_message_reply_media: messageMedia,
                    bot_message_reply_origin: messageOrigin,
                    bot_message_reply_options: messageOptions,
                    endpoint: "log",
                    endpoint_result: "user clicked go back button",
                    session_id: getCurrentSessionId(),
                    is_flagged: "false",
                    has_been_reviewed: "false",
                    found_correct_node: "unknown",
                    elapsed_time_to_fetch_audio: "Already fetched before",
                    elapsed_time_to_fetch_media: "Already fetched before",
                    user_message_sent_datetime: getCurrentPSTDateTime(),
                    user_message: "Go Back",
                    user_email: getUserEmail(),
                    user_replying_to_node_uuid: userReplyingToNodeUuid,
                  };

              try {
                await makeLogRequest(finalLogPayload);
              } catch (e) {
                console.warn("Failed to log go back:", e);
              }

              // Create a new message with the same content but new ID
              const newMessage: ChatMessage = {
                ...messageItem,
                id: crypto.randomUUID(),
                messageId: userMessageId,
                dateTime: new Date(),
                origin: "Go Back",
              };
              // Add the message back to the chat
              addToStack(secondItem.nodeUuid, secondItem.messageId);
              addMessageObject(newMessage);
            } else {
              addMessage(CHAT_CONSTANTS.MESSAGES.UNABLE_TO_GO_BACK, "bot");
            }
          }
        } catch (error) {
          console.error("Go back request error:", error);
          addMessage(CHAT_CONSTANTS.ERROR_MESSAGE, "bot", userMessageId);
        }
      });
    },
    [addMessage, addMessageObject, setGoBackStack, addToStack]
  );

  const handleRepeat = useCallback(
    async (queryId: string) => {
      if (goBackStackRef.current.length === 0) {
        console.warn("goBackStack is empty, nothing to repeat");
        return;
      }

      const userMessageId = crypto.randomUUID();

      await wrapTyping(async () => {
        try {
          const lastValidItem = [...goBackStackRef.current]
            .reverse()
            .find(
              (item) =>
                item.nodeUuid !== CHAT_CONSTANTS.PLACEHOLDER_UUID &&
                item.nodeUuid !== CHAT_CONSTANTS.NULL_UUID
            );

          if (lastValidItem) {
            const messageItem = messagesRef.current.find(
              (msg) =>
                msg.messageId === lastValidItem.messageId &&
                msg.messageType === "bot"
            );

            if (messageItem) {
              // Get the last bot message to find the node UUID the user is replying to
              const lastBotMessage = getLastBotMessage(messagesRef.current);
              const userReplyingToNodeUuid = lastBotMessage?.nodeUuid || "";

              // Extract primitive values to avoid circular references
              const messageNodeUuid = messageItem.nodeUuid || "";
              const messageText = messageItem.text || "";
              const messageAudio = messageItem.audio
                ? Array.isArray(messageItem.audio)
                  ? messageItem.audio[0]
                  : messageItem.audio
                : "";
              const messageMedia = messageItem.media || "";
              const messageOrigin = messageItem.origin || "";
              const messageOptions = messageItem.options || [];

              // Create a clean payload object to avoid circular references
              const finalLogPayload = {
                query_id: queryId || crypto.randomUUID().toString(),
                elapsed_time_to_fetch_audio: messageAudio
                  ? "Already fetched before"
                  : "",
                elapsed_time_to_fetch_media: messageMedia
                  ? "Already fetched before"
                  : "",
                session_id: getCurrentSessionId(),
                bot_message_reply_uuid: messageNodeUuid,
                bot_message_reply_text: messageText,
                bot_message_reply_audio: messageAudio,
                bot_message_reply_media: messageMedia,
                bot_message_reply_origin: messageOrigin,
                bot_message_reply_options: messageOptions,
                bot_message_reply_datetime: getCurrentPSTDateTime(),
                user_email: getUserEmail(),
                user_replying_to_node_uuid: userReplyingToNodeUuid,
              };

              try {
                await makeLogRequest(finalLogPayload);
              } catch (e) {
                console.warn("Failed to log repeat:", e);
              }

              const newMessage: ChatMessage = {
                ...messageItem,
                id: crypto.randomUUID(),
                messageId: userMessageId,
                dateTime: new Date(),
                origin: "Repeat",
              };

              addToStack(lastValidItem.nodeUuid, lastValidItem.messageId);
              addMessageObject(newMessage);
            } else {
              console.error(
                "Message not found for repeat:",
                lastValidItem.messageId
              );
              addMessage(CHAT_CONSTANTS.MESSAGES.UNABLE_TO_REPEAT, "bot");
            }
          } else {
            console.error("No valid message found to repeat");
            addMessage(CHAT_CONSTANTS.MESSAGES.UNABLE_TO_REPEAT, "bot");
          }
        } catch (error) {
          console.error("Repeat request error:", error);
          addMessage(CHAT_CONSTANTS.ERROR_MESSAGE, "bot", userMessageId);
        }
      });
    },
    [addMessage, addMessageObject, addToStack]
  );

  return { handleGoBack, handleRepeat };
};
